import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { 
  FullScreenModal,
  TextFormatter,
  JSONDiff,
  JWTTool,
  Base64Tool
} from './components/ToolsSimple.js';
import YAMLFormatter from './components/YAMLFormatter.js';
import MarkdownFormatter from './components/MarkdownFormatter.js';
import ThemeSwitcher from './components/ThemeSwitcher.js';
import Home from './components/Home.js';
import ToolSEO from './components/ToolSEO.js';
import { HelmetProvider } from 'react-helmet-async';

// Enhanced Epoch Converter Component with Full Screen Support
const EpochConverter = () => {
  const [mode, setMode] = useState('epochToDate');
  const [epochInput, setEpochInput] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dateTimeInput, setDateTimeInput] = useState('');
  const [epochResult, setEpochResult] = useState(null);
  const [dateError, setDateError] = useState('');
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const convertEpoch = (input) => {
    if (!input || input.trim() === '') {
      setResult(null);
      setError('');
      return;
    }

    const numInput = parseInt(input.trim());
    
    if (isNaN(numInput)) {
      setError('Please enter a valid number');
      setResult(null);
      return;
    }

    try {
      let timestamp;
      let detectedUnit;
      
      if (input.length <= 10) {
        timestamp = numInput * 1000;
        detectedUnit = 'seconds';
      } else {
        timestamp = numInput;
        detectedUnit = 'milliseconds';
      }

      const date = new Date(timestamp);
      
      if (isNaN(date.getTime()) || date.getFullYear() < 1970 || date.getFullYear() > 2100) {
        setError('Invalid timestamp - please check your input');
        setResult(null);
        return;
      }

      const utcString = date.toUTCString();
      const localString = date.toString();
      const isoString = date.toISOString();
      
      setResult({
        detectedUnit,
        utc: utcString,
        local: localString,
        iso: isoString,
        originalInput: input
      });
      setError('');
    } catch (err) {
      setError('Error converting timestamp');
      setResult(null);
    }
  };

  const convertDateTime = (dateTimeStr) => {
    if (!dateTimeStr || dateTimeStr.trim() === '') {
      setEpochResult(null);
      setDateError('');
      return;
    }

    try {
      const date = new Date(dateTimeStr);
      
      if (isNaN(date.getTime())) {
        setDateError('Please enter a valid date and time');
        setEpochResult(null);
        return;
      }

      const epochSeconds = Math.floor(date.getTime() / 1000);
      const epochMilliseconds = date.getTime();
      
      setEpochResult({
        seconds: epochSeconds,
        milliseconds: epochMilliseconds,
        utc: date.toUTCString(),
        local: date.toString(),
        iso: date.toISOString()
      });
      setDateError('');
    } catch (err) {
      setDateError('Error converting date/time');
      setEpochResult(null);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      convertEpoch(epochInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [epochInput]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      convertDateTime(dateTimeInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [dateTimeInput]);

  const getCurrentEpoch = () => {
    const now = Date.now();
    setEpochInput(now.toString());
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setDateTimeInput(localDateTime);
  };

  const toggleMode = () => {
    const newMode = mode === 'epochToDate' ? 'dateToEpoch' : 'epochToDate';
    setMode(newMode);
    
    setResult(null);
    setEpochResult(null);
    setError('');
    setDateError('');
    
    if (newMode === 'epochToDate') {
      const now = Date.now();
      setEpochInput(now.toString());
      setDateTimeInput('');
    } else {
      const now = new Date();
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setDateTimeInput(localDateTime);
      setEpochInput('');
    }
  };

  useEffect(() => {
    const now = Date.now();
    setEpochInput(now.toString());
  }, []);

  const getOutputContent = () => {
    if (mode === 'epochToDate' && result) {
      return `UTC Time: ${result.utc}\nLocal Time: ${result.local}\nISO 8601: ${result.iso}`;
    } else if (mode === 'dateToEpoch' && epochResult) {
      return `Epoch (Seconds): ${epochResult.seconds}\nEpoch (Milliseconds): ${epochResult.milliseconds}\nConfirmation: ${epochResult.local}`;
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">Epoch Converter</h1>
            <p className="text-gray-600 text-sm">Convert between Unix timestamps and human-readable time</p>
          </div>

          <div className="flex justify-center mb-8">
            <div className="bg-stone-100 rounded-full p-1 flex">
              <button
                onClick={() => mode !== 'epochToDate' && toggleMode()}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  mode === 'epochToDate'
                    ? 'bg-stone-500 text-white shadow-sm'
                    : 'text-stone-700 hover:text-stone-900'
                }`}
              >
                Epoch → Date
              </button>
              <button
                onClick={() => mode !== 'dateToEpoch' && toggleMode()}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  mode === 'dateToEpoch'
                    ? 'bg-stone-500 text-white shadow-sm'
                    : 'text-stone-700 hover:text-stone-900'
                }`}
              >
                Date → Epoch
              </button>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            {mode === 'epochToDate' ? (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-medium text-gray-800 mb-2">Convert Epoch to Date</h2>
                  <p className="text-gray-500 text-sm">Enter Unix timestamp (seconds or milliseconds)</p>
                </div>

                <div>
                  <input
                    type="text"
                    value={epochInput}
                    onChange={(e) => setEpochInput(e.target.value)}
                    placeholder="1609459200000"
                    className="w-full px-6 py-4 text-lg border border-amber-200 bg-amber-50 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent transition-all duration-200 text-center"
                  />
                  
                  <div className="text-center mt-4">
                    <button
                      onClick={getCurrentEpoch}
                      className="text-sm text-amber-700 hover:text-amber-900 underline transition-colors duration-200"
                    >
                      Use current timestamp
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-center">
                    <p className="text-red-600 text-sm bg-red-50 inline-block px-4 py-2 rounded-lg">
                      {error}
                    </p>
                  </div>
                )}

                {result && (
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-6 shadow-sm">
                    <div className="mb-4 text-center">
                      <span className="inline-block bg-stone-100 text-stone-800 text-xs px-3 py-1 rounded-full">
                        Detected as {result.detectedUnit}
                      </span>
                      <button
                        onClick={() => setIsFullScreenOpen(true)}
                        className="ml-2 px-3 py-1 text-xs bg-stone-600 text-white rounded hover:bg-stone-700 transition-colors"
                        title="Expand to full screen"
                      >
                        🔍 Expand
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">UTC Time</label>
                        <div className="bg-white p-3 rounded border border-stone-200 text-gray-800 font-mono text-sm">
                          {result.utc}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Local Time</label>
                        <div className="bg-white p-3 rounded border border-stone-200 text-gray-800 font-mono text-sm">
                          {result.local}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ISO 8601</label>
                        <div className="bg-white p-3 rounded border border-stone-200 text-gray-800 font-mono text-sm">
                          {result.iso}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-medium text-gray-800 mb-2">Convert Date to Epoch</h2>
                  <p className="text-gray-500 text-sm">Select date and time</p>
                </div>

                <div>
                  <input
                    type="datetime-local"
                    value={dateTimeInput}
                    onChange={(e) => setDateTimeInput(e.target.value)}
                    className="w-full px-6 py-4 text-lg border border-green-200 bg-green-50 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-transparent transition-all duration-200 text-center"
                  />
                  
                  <div className="text-center mt-4">
                    <button
                      onClick={getCurrentDateTime}
                      className="text-sm text-green-600 hover:text-green-800 underline transition-colors duration-200"
                    >
                      Use current date/time
                    </button>
                  </div>
                </div>

                {dateError && (
                  <div className="text-center">
                    <p className="text-red-600 text-sm bg-red-50 inline-block px-4 py-2 rounded-lg">
                      {dateError}
                    </p>
                  </div>
                )}

                {epochResult && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 shadow-sm">
                    <div className="mb-4 text-center">
                      <button
                        onClick={() => setIsFullScreenOpen(true)}
                        className="px-3 py-1 text-xs bg-purple-400 text-white rounded hover:bg-purple-500 transition-colors"
                        title="Expand to full screen"
                      >
                        🔍 Expand
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Epoch (Seconds)</label>
                        <div className="bg-white p-3 rounded border border-blue-200 text-gray-800 font-mono text-sm">
                          {epochResult.seconds}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Epoch (Milliseconds)</label>
                        <div className="bg-white p-3 rounded border border-blue-200 text-gray-800 font-mono text-sm">
                          {epochResult.milliseconds}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation</label>
                        <div className="bg-white p-3 rounded border border-blue-200 text-gray-800 font-mono text-sm">
                          {epochResult.local}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Screen Modal */}
      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        title={`Epoch Converter - ${mode === 'epochToDate' ? 'Timestamp to Date' : 'Date to Timestamp'}`}
        copyText={getOutputContent()}
        content={
          <textarea
            value={getOutputContent()}
            readOnly
            className="w-full h-full resize-none font-mono text-sm p-4 border-none focus:outline-none bg-gray-50"
          />
        }
      />

      <footer className="py-6 text-center">
        <p className="text-xs text-gray-500">
          Convert between Unix timestamps and human-readable dates • Toggle between modes
        </p>
      </footer>
    </div>
  );
};

// Enhanced JSON Formatter Component with Full Screen Support
const JSONFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('format');
  const [viewMode, setViewMode] = useState('text');
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const sampleJSON = {
    "users": [
      {
        "id": 1,
        "name": "John Doe",
        "email": "john.doe@example.com",
        "age": 30,
        "address": {
          "street": "123 Main St",
          "city": "New York",
          "state": "NY",
          "zipCode": "10001",
          "country": "USA"
        },
        "hobbies": ["reading", "swimming", "coding"],
        "isActive": true,
        "registeredAt": "2023-01-15T08:30:00Z",
        "metadata": {
          "lastLogin": "2024-01-10T14:22:33Z",
          "loginCount": 145,
          "preferences": {
            "theme": "dark",
            "notifications": true,
            "language": "en"
          }
        }
      },
      {
        "id": 2,
        "name": "Jane Smith",
        "email": "jane.smith@example.com",
        "age": 28,
        "address": {
          "street": "456 Oak Avenue",
          "city": "Los Angeles",
          "state": "CA",
          "zipCode": "90001",
          "country": "USA"
        },
        "hobbies": ["painting", "yoga", "traveling"],
        "isActive": true,
        "registeredAt": "2023-03-20T10:15:00Z",
        "metadata": {
          "lastLogin": "2024-01-11T09:45:12Z",
          "loginCount": 89,
          "preferences": {
            "theme": "light",
            "notifications": false,
            "language": "en"
          }
        }
      },
      {
        "id": 3,
        "name": "Bob Johnson",
        "email": "bob.johnson@example.com",
        "age": 35,
        "address": {
          "street": "789 Pine Road",
          "city": "Chicago",
          "state": "IL",
          "zipCode": "60601",
          "country": "USA"
        },
        "hobbies": ["photography", "hiking", "cooking"],
        "isActive": false,
        "registeredAt": "2022-11-05T16:45:00Z",
        "metadata": {
          "lastLogin": "2023-12-28T18:30:45Z",
          "loginCount": 234,
          "preferences": {
            "theme": "dark",
            "notifications": true,
            "language": "en"
          }
        }
      }
    ],
    "products": [
      {
        "id": 101,
        "name": "Laptop Pro 15",
        "category": "Electronics",
        "price": 1299.99,
        "inStock": true,
        "specs": {
          "processor": "Intel i7",
          "ram": "16GB",
          "storage": "512GB SSD",
          "display": "15.6 inch Retina"
        },
        "tags": ["computer", "professional", "portable"]
      },
      {
        "id": 102,
        "name": "Wireless Mouse",
        "category": "Accessories",
        "price": 29.99,
        "inStock": true,
        "specs": {
          "connectivity": "Bluetooth 5.0",
          "battery": "Rechargeable",
          "dpi": "1600"
        },
        "tags": ["mouse", "wireless", "ergonomic"]
      },
      {
        "id": 103,
        "name": "Mechanical Keyboard",
        "category": "Accessories",
        "price": 149.99,
        "inStock": false,
        "specs": {
          "switches": "Cherry MX Blue",
          "backlight": "RGB",
          "layout": "Full-size"
        },
        "tags": ["keyboard", "mechanical", "gaming"]
      }
    ],
    "company": {
      "name": "TechCorp Solutions",
      "founded": 2015,
      "employees": 250,
      "headquarters": {
        "street": "100 Tech Plaza",
        "city": "San Francisco",
        "state": "CA",
        "zipCode": "94105",
        "country": "USA"
      },
      "departments": [
        {
          "name": "Engineering",
          "employeeCount": 120,
          "manager": "Alice Williams"
        },
        {
          "name": "Sales",
          "employeeCount": 80,
          "manager": "Charlie Brown"
        },
        {
          "name": "Support",
          "employeeCount": 50,
          "manager": "Diana Prince"
        }
      ]
    }
  };

  const createTreeNode = (value, key = null, path = '', level = 0) => {
    const nodeId = path || 'root';
    
    if (value === null) {
      return { id: nodeId, key, value: 'null', type: 'null', level, children: [] };
    }
    
    if (typeof value !== 'object') {
      const displayValue = typeof value === 'string' ? `"${value}"` : String(value);
      return { id: nodeId, key, value: displayValue, type: typeof value, level, children: [] };
    }
    
    const children = [];
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const childPath = `${path}.${index}`;
        children.push(createTreeNode(item, `[${index}]`, childPath, level + 1));
      });
      return {
        id: nodeId,
        key,
        value: `Array(${value.length})`,
        type: 'array',
        level,
        children,
        isExpandable: children.length > 0
      };
    } else {
      Object.entries(value).forEach(([objKey, objValue]) => {
        const childPath = `${path}.${objKey}`;
        children.push(createTreeNode(objValue, objKey, childPath, level + 1));
      });
      return {
        id: nodeId,
        key,
        value: 'Object',
        type: 'object',
        level,
        children,
        isExpandable: children.length > 0
      };
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    const getAllNodeIds = (node) => {
      const ids = [node.id];
      node.children.forEach(child => {
        ids.push(...getAllNodeIds(child));
      });
      return ids;
    };
    
    if (treeData) {
      const allIds = getAllNodeIds(treeData);
      setExpandedNodes(new Set(allIds));
    }
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const TreeNode = ({ node }) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    
    return (
      <div>
        <div 
          className="flex items-center py-1 hover:bg-gray-100 rounded px-2 cursor-pointer"
          style={{ paddingLeft: `${node.level * 20 + 8}px` }}
          onClick={() => hasChildren && toggleNode(node.id)}
        >
          {hasChildren ? (
            <span className="w-4 h-4 flex items-center justify-center text-gray-500 mr-2">
              {isExpanded ? '▼' : '▶'}
            </span>
          ) : (
            <span className="w-4 h-4 mr-2"></span>
          )}
          
          <span className="text-blue-600 font-medium mr-2">
            {node.key && `${node.key}:`}
          </span>
          
          <span className={`${
            node.type === 'string' ? 'text-green-600' :
            node.type === 'number' ? 'text-purple-600' :
            node.type === 'boolean' ? 'text-orange-600' :
            node.type === 'null' ? 'text-gray-500' :
            'text-gray-800'
          }`}>
            {node.value}
          </span>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child, index) => (
              <TreeNode key={`${child.id}-${index}`} node={child} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatJSON = () => {
    if (!input.trim()) {
      setError('Please enter some JSON data');
      setOutput('');
      setTreeData(null);
      return;
    }

    try {
      const parsed = JSON.parse(input);
      
      if (viewMode === 'tree') {
        const treeNodeData = createTreeNode(parsed);
        setTreeData(treeNodeData);
        setExpandedNodes(new Set(['root']));
        setOutput('');
      } else {
        const formatted = JSON.stringify(parsed, null, 2);
        setOutput(formatted);
        setTreeData(null);
      }
      
      setError('');
      setMode('format');
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
      setOutput('');
      setTreeData(null);
    }
  };

  const minifyJSON = () => {
    if (!input.trim()) {
      setError('Please enter some JSON data');
      setOutput('');
      return;
    }

    try {
      const parsed = JSON.parse(input);
      const minified = JSON.stringify(parsed);
      setOutput(minified);
      setError('');
      setMode('minify');
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
      setOutput('');
    }
  };

  const validateJSON = () => {
    if (!input.trim()) {
      setError('Please enter some JSON data');
      setOutput('');
      return;
    }

    try {
      JSON.parse(input);
      setOutput('✅ Valid JSON');
      setError('');
      setMode('validate');
    } catch (err) {
      setError('❌ Invalid JSON: ' + err.message);
      setOutput('');
    }
  };

  const escapeJSON = () => {
    if (!input.trim()) {
      setError('Please enter some JSON data');
      setOutput('');
      return;
    }

    try {
      // First validate that it's valid JSON
      JSON.parse(input);
      
      // Escape the JSON string
      const escaped = JSON.stringify(input);
      setOutput(escaped);
      setError('');
      setMode('escape');
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
      setOutput('');
    }
  };

  const unescapeJSON = () => {
    if (!input.trim()) {
      setError('Please enter some JSON string');
      setOutput('');
      return;
    }

    try {
      // Try to parse the escaped JSON string
      const unescaped = JSON.parse(input);
      
      // If the result is a string, set it as output
      if (typeof unescaped === 'string') {
        setOutput(unescaped);
        setError('');
        setMode('unescape');
      } else {
        // If it's already an object/array, format it nicely
        const formatted = JSON.stringify(unescaped, null, 2);
        setOutput(formatted);
        setError('');
        setMode('unescape');
      }
    } catch (err) {
      setError('Invalid escaped JSON string: ' + err.message);
      setOutput('');
    }
  };

  const loadSample = () => {
    const sampleStr = JSON.stringify(sampleJSON, null, 2);
    setInput(sampleStr);
    setOutput('');
    setError('');
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    setTreeData(null);
    setExpandedNodes(new Set());
    setViewMode('text');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="w-full px-4 mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">JSON Formatter</h1>
            <p className="text-gray-600 text-sm">Format, validate, and transform JSON data</p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <button onClick={formatJSON} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Format</button>
            <button onClick={minifyJSON} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Minify</button>
            <button onClick={validateJSON} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Validate</button>
            <button onClick={escapeJSON} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Escape</button>
            <button onClick={unescapeJSON} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Unescape</button>
            <button onClick={loadSample} className="px-4 py-2 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm">Load Sample</button>
            <button onClick={clearAll} className="px-4 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm">Clear All</button>
          </div>

          {error && (
            <div className="text-center mb-6">
              <p className="text-red-600 text-sm bg-red-50 inline-block px-4 py-2 rounded-lg">{error}</p>
            </div>
          )}

          <div className="flex gap-6">
            <div className="w-1/4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Input</label>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Enter your JSON data here..."
                  className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-amber-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm bg-amber-50"
                />
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">{input.length} characters</p>
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Output
                    {mode && (
                      <span className="ml-2 inline-block bg-stone-100 text-stone-800 text-xs px-2 py-1 rounded-full">
                        {mode === 'format' && (viewMode === 'tree' ? 'Tree View' : 'Formatted')}
                        {mode === 'minify' && 'Minified'}
                        {mode === 'validate' && 'Validation'}
                        {mode === 'escape' && 'Escaped'}
                        {mode === 'unescape' && 'Unescaped'}
                      </span>
                    )}
                  </label>
                  
                  <div className="flex items-center space-x-2">
                    {mode === 'format' && (input.trim() || output || treeData) && (
                      <div className="flex bg-stone-100 rounded-lg p-1">
                        <button
                          onClick={() => {
                            setViewMode('text');
                            if (input.trim()) {
                              try {
                                const parsed = JSON.parse(input);
                                const formatted = JSON.stringify(parsed, null, 2);
                                setOutput(formatted);
                                setTreeData(null);
                              } catch (err) {}
                            }
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${
                            viewMode === 'text'
                              ? 'bg-stone-500 text-white shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Text
                        </button>
                        <button
                          onClick={() => {
                            setViewMode('tree');
                            if (input.trim()) {
                              try {
                                const parsed = JSON.parse(input);
                                const treeNodeData = createTreeNode(parsed);
                                setTreeData(treeNodeData);
                                setExpandedNodes(new Set(['root']));
                                setOutput('');
                              } catch (err) {}
                            }
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${
                            viewMode === 'tree'
                              ? 'bg-stone-500 text-white shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Tree
                        </button>
                      </div>
                    )}
                    
                    {(output || treeData) && (
                      <button
                        onClick={() => setIsFullScreenOpen(true)}
                        className="px-3 py-1 text-xs bg-stone-600 text-white rounded hover:bg-stone-700 transition-colors"
                        title="Expand to full screen"
                      >
                        🔍 Expand
                      </button>
                    )}
                  </div>
                </div>
                
                {mode === 'format' && viewMode === 'tree' && treeData ? (
                  <div className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-stone-200 rounded-lg shadow-sm bg-stone-50 overflow-auto">
                    <div className="flex justify-end space-x-2 mb-4">
                      <button onClick={expandAll} className="px-3 py-1 text-xs bg-amber-100 text-amber-800 rounded hover:bg-amber-200">Expand All</button>
                      <button onClick={collapseAll} className="px-3 py-1 text-xs bg-stone-200 text-stone-800 rounded hover:bg-stone-300">Collapse All</button>
                    </div>
                    <TreeNode node={treeData} />
                  </div>
                ) : (
                  <textarea
                    value={output}
                    readOnly
                    placeholder="Processed JSON will appear here..."
                    className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-stone-200 rounded-lg shadow-sm bg-stone-50 resize-none font-mono text-sm whitespace-pre-wrap"
                  />
                )}
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">{output.length} characters</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        title={`JSON Formatter - ${mode === 'format' && viewMode === 'tree' ? 'Tree View' : mode.charAt(0).toUpperCase() + mode.slice(1)}`}
        copyText={output}
        content={
          mode === 'format' && viewMode === 'tree' && treeData ? (
            <div className="w-full h-full bg-gray-50 overflow-auto p-4 font-mono text-sm">
              <div className="flex justify-end space-x-2 mb-4">
                <button onClick={expandAll} className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">Expand All</button>
                <button onClick={collapseAll} className="px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded hover:bg-gray-200">Collapse All</button>
              </div>
              <TreeNode node={treeData} />
            </div>
          ) : (
            <textarea
              value={output}
              readOnly
              className="w-full h-full resize-none font-mono text-sm p-4 border-none focus:outline-none bg-gray-50"
            />
          )
        }
      />

      <footer className="py-6 text-center">
        <p className="text-xs text-gray-500">Format, validate, and transform JSON data with ease</p>
      </footer>
    </div>
  );
};

// Enhanced Text Diff Component - Professional Git-style Diff
const TextDiff = () => {
  const [leftInput, setLeftInput] = useState('');
  const [rightInput, setRightInput] = useState('');
  const [diffResult, setDiffResult] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [leftFormattedDiff, setLeftFormattedDiff] = useState([]);
  const [rightFormattedDiff, setRightFormattedDiff] = useState([]);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [diffViewMode, setDiffViewMode] = useState('side-by-side');

  const sampleLeft = `function calculateTotal(items) {
  let total = 0;
  for (let i = 0; i < items.length; i++) {
    total += items[i].price;
  }
  return total;
}

const data = {
  products: [
    { name: "Laptop", price: 999 },
    { name: "Mouse", price: 25 }
  ]
};

console.log("Total:", calculateTotal(data.products));`;

  const sampleRight = `function calculateTotal(items) {
  let total = 0;
  for (let i = 0; i < items.length; i++) {
    total += items[i].price * items[i].quantity;
  }
  return total;
}

const data = {
  products: [
    { name: "Laptop", price: 999, quantity: 1 },
    { name: "Mouse", price: 25, quantity: 2 },
    { name: "Keyboard", price: 75, quantity: 1 }
  ]
};

console.log("Total:", calculateTotal(data.products));
console.log("Items count:", data.products.length);`;

  // Enhanced diff algorithm
  const computeTextDiff = (text1, text2) => {
    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');
    
    const result = {
      added: [],
      removed: [],
      modified: [],
      unchanged: [],
      stats: {
        totalLines1: lines1.length,
        totalLines2: lines2.length,
        addedCount: 0,
        removedCount: 0,
        modifiedCount: 0
      }
    };

    const line2ToIndices = new Map();
    lines2.forEach((line, index) => {
      const trimmed = line.trim();
      if (!line2ToIndices.has(trimmed)) {
        line2ToIndices.set(trimmed, []);
      }
      line2ToIndices.get(trimmed).push(index);
    });

    const used2 = new Set();
    let i = 0, j = 0;

    while (i < lines1.length || j < lines2.length) {
      if (i >= lines1.length) {
        while (j < lines2.length) {
          if (!used2.has(j)) {
            result.added.push({ 
              line: lines2[j], 
              lineNumber: j + 1, 
              leftIndex: null, 
              rightIndex: j 
            });
            result.stats.addedCount++;
          }
          j++;
        }
        break;
      }

      if (j >= lines2.length) {
        while (i < lines1.length) {
          result.removed.push({ 
            line: lines1[i], 
            lineNumber: i + 1, 
            leftIndex: i, 
            rightIndex: null 
          });
          result.stats.removedCount++;
          i++;
        }
        break;
      }

      const line1 = lines1[i];
      const line2 = lines2[j];

      if (line1 === line2) {
        result.unchanged.push({ 
          line: line1, 
          lineNumber: Math.max(i, j) + 1, 
          leftIndex: i, 
          rightIndex: j 
        });
        used2.add(j);
        i++;
        j++;
      } else {
        const trimmed1 = line1.trim();
        const matches = line2ToIndices.get(trimmed1) || [];
        let foundMatch = false;

        for (const matchIndex of matches) {
          if (matchIndex >= j && !used2.has(matchIndex)) {
            for (let k = j; k < matchIndex; k++) {
              if (!used2.has(k)) {
                result.added.push({ 
                  line: lines2[k], 
                  lineNumber: k + 1, 
                  leftIndex: null, 
                  rightIndex: k 
                });
                result.stats.addedCount++;
                used2.add(k);
              }
            }
            result.unchanged.push({ 
              line: line1, 
              lineNumber: Math.max(i, matchIndex) + 1, 
              leftIndex: i, 
              rightIndex: matchIndex 
            });
            used2.add(matchIndex);
            i++;
            j = matchIndex + 1;
            foundMatch = true;
            break;
          }
        }

        if (!foundMatch) {
          let line2FoundLater = false;
          for (let k = i + 1; k < Math.min(i + 5, lines1.length); k++) {
            if (lines1[k] === line2) {
              line2FoundLater = true;
              break;
            }
          }

          if (line2FoundLater) {
            result.removed.push({ 
              line: line1, 
              lineNumber: i + 1, 
              leftIndex: i, 
              rightIndex: null 
            });
            result.stats.removedCount++;
            i++;
          } else {
            result.modified.push({ 
              oldLine: line1, 
              newLine: line2, 
              lineNumber: Math.max(i, j) + 1, 
              leftIndex: i, 
              rightIndex: j 
            });
            result.stats.modifiedCount++;
            used2.add(j);
            i++;
            j++;
          }
        }
      }
    }

    return result;
  };

  const formatTextWithDiff = (text, diffResult, side = 'left') => {
    const lines = text.split('\n');
    const formattedLines = [];

    lines.forEach((line, index) => {
      let status = 'unchanged';
      let prefix = '  ';
      let diffInfo = null;
      
      if (side === 'left') {
        const removedItem = diffResult.removed.find(item => item.leftIndex === index);
        if (removedItem) {
          status = 'removed';
          prefix = '- ';
          diffInfo = removedItem;
        } else {
          const modifiedItem = diffResult.modified.find(item => item.leftIndex === index);
          if (modifiedItem) {
            status = 'modified';
            prefix = '~ ';
            diffInfo = modifiedItem;
          }
        }
      } else {
        const addedItem = diffResult.added.find(item => item.rightIndex === index);
        if (addedItem) {
          status = 'added';
          prefix = '+ ';
          diffInfo = addedItem;
        } else {
          const modifiedItem = diffResult.modified.find(item => item.rightIndex === index);
          if (modifiedItem) {
            status = 'modified';
            prefix = '~ ';
            diffInfo = modifiedItem;
          }
        }
      }

      formattedLines.push({
        line: prefix + line,
        originalLine: line,
        status,
        lineNumber: index + 1,
        diffInfo
      });
    });

    return formattedLines;
  };

  const generateUnifiedDiff = () => {
    if (!diffResult) return [];
    
    const unified = [];
    const lines1 = leftInput.split('\n');
    const lines2 = rightInput.split('\n');
    
    const changes = [
      ...diffResult.added.map(item => ({ ...item, type: 'added' })),
      ...diffResult.removed.map(item => ({ ...item, type: 'removed' })),
      ...diffResult.modified.map(item => ({ ...item, type: 'modified' }))
    ].sort((a, b) => {
      const aIndex = a.leftIndex !== null ? a.leftIndex : a.rightIndex;
      const bIndex = b.leftIndex !== null ? b.leftIndex : b.rightIndex;
      return aIndex - bIndex;
    });

    let currentIndex = 0;
    
    changes.forEach((change, i) => {
      const changeIndex = change.leftIndex !== null ? change.leftIndex : change.rightIndex;
      
      const contextBefore = Math.max(0, changeIndex - 3);
      for (let j = Math.max(currentIndex, contextBefore); j < changeIndex; j++) {
        if (j < lines1.length) {
          unified.push({
            line: '  ' + lines1[j],
            type: 'context',
            lineNumber: j + 1
          });
        }
      }
      
      if (change.type === 'removed') {
        unified.push({
          line: '- ' + change.line,
          type: 'removed',
          lineNumber: change.lineNumber
        });
      } else if (change.type === 'added') {
        unified.push({
          line: '+ ' + change.line,
          type: 'added',
          lineNumber: change.lineNumber
        });
      } else if (change.type === 'modified') {
        unified.push({
          line: '- ' + change.oldLine,
          type: 'removed',
          lineNumber: change.lineNumber
        });
        unified.push({
          line: '+ ' + change.newLine,
          type: 'added',
          lineNumber: change.lineNumber
        });
      }
      
      currentIndex = changeIndex + 1;
    });

    return unified;
  };

  const compareText = () => {
    const diff = computeTextDiff(leftInput, rightInput);
    setDiffResult(diff);
    setShowDiff(true);
    setIsComparing(true);
    
    const leftDiff = formatTextWithDiff(leftInput, diff, 'left');
    const rightDiff = formatTextWithDiff(rightInput, diff, 'right');
    
    setLeftFormattedDiff(leftDiff);
    setRightFormattedDiff(rightDiff);
  };

  const resetComparison = () => {
    setIsComparing(false);
    setShowDiff(false);
    setDiffResult(null);
    setLeftFormattedDiff([]);
    setRightFormattedDiff([]);
  };

  const loadSample = () => {
    setLeftInput(sampleLeft);
    setRightInput(sampleRight);
    resetComparison();
  };

  const clearAll = () => {
    setLeftInput('');
    setRightInput('');
    resetComparison();
  };

  const getUnifiedDiffText = () => {
    const unified = generateUnifiedDiff();
    return unified.map(item => item.line).join('\n');
  };

  const getSideBySideDiffText = () => {
    const leftText = leftFormattedDiff.map(item => item.line).join('\n');
    const rightText = rightFormattedDiff.map(item => item.line).join('\n');
    return `--- Left (Original)\n${leftText}\n\n+++ Right (Modified)\n${rightText}`;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="w-full px-4 mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">Text Diff</h1>
            <p className="text-gray-600 text-sm">Compare text files with professional git-style diff highlighting</p>
          </div>

          {isComparing && (
            <div className="flex justify-center mb-6">
              <div className="bg-stone-100 rounded-full p-1 flex">
                <button
                  onClick={() => setDiffViewMode('side-by-side')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    diffViewMode === 'side-by-side'
                      ? 'bg-stone-500 text-white shadow-sm'
                      : 'text-stone-700 hover:text-stone-900'
                  }`}
                >
                  Side by Side
                </button>
                <button
                  onClick={() => setDiffViewMode('unified')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    diffViewMode === 'unified'
                      ? 'bg-stone-500 text-white shadow-sm'
                      : 'text-stone-700 hover:text-stone-900'
                  }`}
                >
                  Unified Diff
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-4 mb-8">
            {!isComparing ? (
              <>
                <button
                  onClick={compareText}
                  disabled={!leftInput.trim() || !rightInput.trim()}
                  className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Compare Text
                </button>
                <button
                  onClick={loadSample}
                  className="px-4 py-3 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Load Sample
                </button>
                <button
                  onClick={clearAll}
                  className="px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Clear All
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={resetComparison}
                  className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Reset & Edit Again
                </button>
                <button
                  onClick={() => setIsFullScreenOpen(true)}
                  className="px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  🔍 Full Screen Diff
                </button>
                <button
                  onClick={clearAll}
                  className="px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Clear All
                </button>
              </>
            )}
          </div>

          {isComparing && diffResult && (
            <div className="mb-6 text-center">
              <div className="inline-flex items-center space-x-4 bg-blue-50 border border-blue-200 rounded-lg px-6 py-3">
                <span className="text-blue-800 font-medium">Diff Results:</span>
                <span className="text-green-600 font-semibold">{diffResult.stats.addedCount} Added</span>
                <span className="text-red-600 font-semibold">{diffResult.stats.removedCount} Removed</span>
                <span className="text-orange-600 font-semibold">{diffResult.stats.modifiedCount} Modified</span>
                <span className="text-gray-600 font-semibold">{diffResult.unchanged.length} Unchanged</span>
              </div>
            </div>
          )}

          {!isComparing || diffViewMode === 'side-by-side' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Left Text (Original)
                    {isComparing && (
                      <span className="ml-2 inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                        Showing Removals & Modifications
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    {!isComparing ? (
                      <textarea
                        value={leftInput}
                        onChange={(e) => setLeftInput(e.target.value)}
                        placeholder="Enter your first text here..."
                        className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-amber-200 bg-amber-50 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm"
                      />
                    ) : (
                      <div className="w-full h-[calc(100vh-320px)] border-2 border-stone-300 rounded-lg shadow-lg bg-stone-50 overflow-auto font-mono text-sm relative">
                        <div className="sticky top-0 bg-stone-200 text-stone-800 text-xs px-2 py-1 font-medium border-b border-stone-300">
                          Lines: {diffResult.stats.totalLines1} | Removed: {diffResult.stats.removedCount} | Modified: {diffResult.stats.modifiedCount}
                        </div>
                        <div className="p-4">
                          {leftFormattedDiff.map((item, index) => (
                            <div 
                              key={index} 
                              className={`whitespace-pre leading-relaxed py-1 transition-all duration-200 ${
                                item.status === 'removed' ? 'text-red-700 bg-red-100 px-1 rounded font-medium border-l-2 border-red-400' :
                                item.status === 'modified' ? 'text-orange-700 bg-orange-100 px-1 rounded font-medium border-l-2 border-orange-400' :
                                'hover:bg-stone-100'
                              }`}
                            >
                              <span className="select-none text-gray-400 mr-2 text-xs">
                                {String(item.lineNumber).padStart(3, ' ')}
                              </span>
                              {item.line}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-center mt-2">
                    <p className="text-gray-500 text-xs">
                      {leftInput.length} characters • {leftInput.split('\n').length} lines
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Right Text (Modified)
                    {isComparing && (
                      <span className="ml-2 inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Showing Additions & Modifications
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    {!isComparing ? (
                      <textarea
                        value={rightInput}
                        onChange={(e) => setRightInput(e.target.value)}
                        placeholder="Enter your second text here..."
                        className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-amber-200 bg-amber-50 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm"
                      />
                    ) : (
                      <div className="w-full h-[calc(100vh-320px)] border-2 border-stone-300 rounded-lg shadow-lg bg-stone-50 overflow-auto font-mono text-sm relative">
                        <div className="sticky top-0 bg-stone-200 text-stone-800 text-xs px-2 py-1 font-medium border-b border-stone-300">
                          Lines: {diffResult.stats.totalLines2} | Added: {diffResult.stats.addedCount} | Modified: {diffResult.stats.modifiedCount}
                        </div>
                        <div className="p-4">
                          {rightFormattedDiff.map((item, index) => (
                            <div 
                              key={index} 
                              className={`whitespace-pre leading-relaxed py-1 transition-all duration-200 ${
                                item.status === 'added' ? 'text-green-700 bg-green-100 px-1 rounded font-medium border-l-2 border-green-400' :
                                item.status === 'modified' ? 'text-orange-700 bg-orange-100 px-1 rounded font-medium border-l-2 border-orange-400' :
                                'hover:bg-stone-100'
                              }`}
                            >
                              <span className="select-none text-gray-400 mr-2 text-xs">
                                {String(item.lineNumber).padStart(3, ' ')}
                              </span>
                              {item.line}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-center mt-2">
                    <p className="text-gray-500 text-xs">
                      {rightInput.length} characters • {rightInput.split('\n').length} lines
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-8">
              <div className="w-full h-[600px] border-2 border-purple-400 rounded-lg shadow-lg bg-gray-50 overflow-auto font-mono text-sm">
                <div className="sticky top-0 bg-purple-100 text-purple-800 text-xs px-4 py-2 font-medium border-b flex justify-between">
                  <span>Unified Diff View</span>
                  <span>Total Changes: {diffResult.stats.addedCount + diffResult.stats.removedCount + diffResult.stats.modifiedCount}</span>
                </div>
                <div className="p-4">
                  {generateUnifiedDiff().map((item, index) => (
                    <div 
                      key={index} 
                      className={`whitespace-pre leading-relaxed py-1 ${
                        item.type === 'added' ? 'text-green-700 bg-green-100 border-l-2 border-green-500 px-1' :
                        item.type === 'removed' ? 'text-red-700 bg-red-100 border-l-2 border-red-500 px-1' :
                        item.type === 'context' ? 'text-gray-600' :
                        ''
                      }`}
                    >
                      {item.line}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Git-Style Diff Legend:</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-center space-x-2">
                <span className="text-green-700 font-mono text-sm bg-green-100 px-2 py-1 rounded border-l-2 border-green-500">+ Added</span>
                <span className="text-sm text-gray-600">Lines only in right</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <span className="text-red-700 font-mono text-sm bg-red-100 px-2 py-1 rounded border-l-2 border-red-500">- Removed</span>
                <span className="text-sm text-gray-600">Lines only in left</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <span className="text-orange-700 font-mono text-sm bg-orange-100 px-2 py-1 rounded border-l-2 border-orange-500">~ Modified</span>
                <span className="text-sm text-gray-600">Changed lines</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <span className="text-gray-600 font-mono text-sm px-2 py-1">  Context</span>
                <span className="text-sm text-gray-600">Unchanged lines</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        title={`Text Diff - ${diffViewMode === 'unified' ? 'Unified View' : 'Side by Side View'}`}
        copyText={diffViewMode === 'unified' ? getUnifiedDiffText() : getSideBySideDiffText()}
        content={
          diffViewMode === 'unified' ? (
            <div className="w-full h-full bg-gray-50 overflow-auto font-mono text-sm">
              <div className="p-4">
                {generateUnifiedDiff().map((item, index) => (
                  <div 
                    key={index} 
                    className={`whitespace-pre leading-relaxed py-1 ${
                      item.type === 'added' ? 'text-green-700 bg-green-100 border-l-2 border-green-500 px-1' :
                      item.type === 'removed' ? 'text-red-700 bg-red-100 border-l-2 border-red-500 px-1' :
                      item.type === 'context' ? 'text-gray-600' :
                      ''
                    }`}
                  >
                    {item.line}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="w-full h-full grid grid-cols-2 gap-4">
              <div className="bg-gray-50 overflow-auto font-mono text-sm border-r">
                <div className="sticky top-0 bg-red-100 text-red-800 text-sm px-4 py-2 font-medium border-b">
                  Left (Original) - {diffResult?.stats.totalLines1} lines
                </div>
                <div className="p-4">
                  {leftFormattedDiff.map((item, index) => (
                    <div 
                      key={index} 
                      className={`whitespace-pre leading-relaxed py-1 ${
                        item.status === 'removed' ? 'text-red-700 bg-red-100 px-1 rounded font-medium border-l-2 border-red-500' :
                        item.status === 'modified' ? 'text-orange-700 bg-orange-100 px-1 rounded font-medium border-l-2 border-orange-500' :
                        ''
                      }`}
                    >
                      <span className="select-none text-gray-400 mr-2 text-xs">
                        {String(item.lineNumber).padStart(3, ' ')}
                      </span>
                      {item.line}
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 overflow-auto font-mono text-sm">
                <div className="sticky top-0 bg-green-100 text-green-800 text-sm px-4 py-2 font-medium border-b">
                  Right (Modified) - {diffResult?.stats.totalLines2} lines
                </div>
                <div className="p-4">
                  {rightFormattedDiff.map((item, index) => (
                    <div 
                      key={index} 
                      className={`whitespace-pre leading-relaxed py-1 ${
                        item.status === 'added' ? 'text-green-700 bg-green-100 px-1 rounded font-medium border-l-2 border-green-500' :
                        item.status === 'modified' ? 'text-orange-700 bg-orange-100 px-1 rounded font-medium border-l-2 border-orange-500' :
                        ''
                      }`}
                    >
                      <span className="select-none text-gray-400 mr-2 text-xs">
                        {String(item.lineNumber).padStart(3, ' ')}
                      </span>
                      {item.line}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        }
      />

      <footer className="py-6 text-center">
        <p className="text-xs text-gray-500">
          Professional text comparison with git-style diff highlighting • Side-by-side and unified views
        </p>
      </footer>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <div className="App">
        <ThemeSwitcher />
        <Routes>
          <Route path="/" element={<><Home /><ToolSEO slug="home" /></>} />
          <Route path="/epoch-converter" element={<><EpochConverter /><ToolSEO slug="epoch-converter" /></>} />
          <Route path="/json-formatter" element={<><JSONFormatter /><ToolSEO slug="json-formatter" /></>} />
          <Route path="/yaml-formatter" element={<><YAMLFormatter /><ToolSEO slug="yaml-formatter" /></>} />
          <Route path="/markdown-formatter" element={<><MarkdownFormatter /><ToolSEO slug="markdown-formatter" /></>} />
          <Route path="/text-diff" element={<><TextDiff /><ToolSEO slug="text-diff" /></>} />
          <Route path="/text-formatter" element={<><TextFormatter /><ToolSEO slug="text-formatter" /></>} />
          <Route path="/json-diff" element={<><JSONDiff /><ToolSEO slug="json-diff" /></>} />
          <Route path="/jwt-tool" element={<><JWTTool /><ToolSEO slug="jwt-tool" /></>} />
          <Route path="/base64-tool" element={<><Base64Tool /><ToolSEO slug="base64-tool" /></>} />
        </Routes>
      </div>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;